import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { Tabs, type TabDef } from "@/components/Tabs";
import { PeopleTab } from "@/components/audit/PeopleTab";
import { SecurityTab } from "@/components/audit/SecurityTab";
import { WorkTab } from "@/components/audit/WorkTab";
import { useWSEvent } from "@/messaging/WSContext";

type TabKey = "work" | "people" | "security";
const TABS: readonly TabDef<TabKey>[] = [
  { key: "work", label: "Work" },
  { key: "people", label: "People" },
  { key: "security", label: "Security" },
];

const RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
] as const;

interface ActorRef {
  id: string | null;
  name: string | null;
  role: string | null;
  online: boolean;
}

/** Team activity, in three views.
 *
 *  The page used to be a paginated dump of `audit_events` — an HTTP access log
 *  in which two thirds of rows were page polls and 99% carried no entity id, so
 *  it could never say *which* deal or client was touched. All three tabs now
 *  read the domain tables instead (`app/services/activity_feed.py`), and the
 *  request log survives as a disclosure inside Security for investigations.
 */
export function Audit() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as TabKey) || "work";
  const range = params.get("range") || (tab === "work" ? "today" : "7d");
  const [actors, setActors] = useState<ActorRef[]>([]);
  // Nudge the Work feed when a teammate does something. The ticker is
  // allow-listed to business actions server-side (app/services/audit.py), so
  // this fires on real work, not on polling.
  const [pulse, setPulse] = useState(0);

  const setParam = useCallback(
    (k: string, v: string) => {
      const next = new URLSearchParams(params);
      next.set(k, v);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  useEffect(() => {
    api<ActorRef[]>("/api/audit/actors").then(setActors).catch(() => {});
  }, []);

  useWSEvent("activity.new", () => setPulse((n) => n + 1));

  return (
    <div className="w-full max-w-[1400px] mx-auto space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs
          tabs={TABS}
          value={tab}
          onChange={(k) => setParam("tab", k)}
          aria-label="Activity views"
        />
        <div className="flex gap-1.5">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setParam("range", r.key)}
              className={`rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium transition ${
                range === r.key
                  ? "bg-white/[0.06] text-slate-200 border border-white/10"
                  : "text-slate-500 hover:text-slate-300 border border-transparent"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "work" && (
        <WorkTab range={range} actors={actors} pulse={pulse} />
      )}
      {tab === "people" && <PeopleTab range={range} />}
      {tab === "security" && <SecurityTab range={range} />}
    </div>
  );
}
